// ============================================
// Constructor Store — Zustand state management
// ============================================

import { create } from 'zustand';

export interface ConstructorBlock {
  id: string;
  component_id: number;
  position: number;
  data: Record<string, any>;
  // Template data (загружается из component_catalog)
  _template_html?: string;
  _template_css?: string;
  _template_js?: string;
}

export interface ConstructorState {
  // Текущий шаг
  step: number;
  totalSteps: number;

  // Данные лендинга
  title: string;
  country: string;
  city: string;
  slug: string;

  // Шаг 1: Бизнес информация
  business_name: string;
  business_description: string;
  phone: string;
  whatsapp: string;
  instagram: string;
  address: string;
  work_hours: string;

  // Шаг 3: Товары/услуги
  items: Array<{ name: string; price: string; description?: string }>;

  // Шаг 4: Цвет и шаблон
  color_scheme: string;
  bg_color: string;
  text_color: string;
  title_color: string;

  // Блоки (компоненты)
  blocks: ConstructorBlock[];

  // Результат
  draft_landing_id: string | null;
  draft_slug: string | null;

  // UI
  language: string;
  isLoading: boolean;
  error: string | null;
  promocode: string;
  promocodeValid: boolean;
  promocodeRemaining: number;

  // Actions
  setStep: (step: number) => void;
  nextStep: () => void;
  prevStep: () => void;

  setField: <K extends keyof ConstructorState>(field: K, value: ConstructorState[K]) => void;

  addItem: () => void;
  removeItem: (index: number) => void;
  updateItem: (index: number, field: string, value: string) => void;

  addBlock: (component: ConstructorBlock) => void;
  removeBlock: (id: string) => void;

  setDraftResult: (landingId: string, slug: string) => void;

  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;

  setPromocode: (code: string) => void;
  setPromocodeValid: (valid: boolean, remaining?: number) => void;

  reset: () => void;

  // Получить все данные лендинга как объект
  getLandingData: () => Record<string, any>;

  // Получить компоненты для отправки
  getComponents: () => Array<{ component_id: number; position: number; data: Record<string, any> }>;
}

const initialState = {
  step: 1,
  totalSteps: 5,
  title: '',
  country: 'kz',
  city: 'astana',
  slug: '',
  business_name: '',
  business_description: '',
  phone: '',
  whatsapp: '',
  instagram: '',
  address: '',
  work_hours: '',
  items: [{ name: '', price: '', description: '' }],
  color_scheme: 'dark',
  bg_color: '#1a1a2e',
  text_color: '#ffffff',
  title_color: '#1a1a2e',
  blocks: [],
  draft_landing_id: null,
  draft_slug: null,
  language: 'ru',
  isLoading: false,
  error: null,
  promocode: '',
  promocodeValid: false,
  promocodeRemaining: 0,
};

export const useConstructorStore = create<ConstructorState>((set, get) => ({
  ...initialState,

  setStep: (step) => set({ step }),
  nextStep: () => set((s) => ({ step: Math.min(s.step + 1, s.totalSteps) })),
  prevStep: () => set((s) => ({ step: Math.max(s.step - 1, 1) })),

  setField: (field, value) => set({ [field]: value }),

  addItem: () => set((s) => ({
    items: [...s.items, { name: '', price: '', description: '' }],
  })),
  removeItem: (index) => set((s) => ({
    items: s.items.filter((_, i) => i !== index),
  })),
  updateItem: (index, field, value) => set((s) => ({
    items: s.items.map((item, i) =>
      i === index ? { ...item, [field]: value } : item
    ),
  })),

  addBlock: (block) => set((s) => ({
    blocks: [...s.blocks, block],
  })),
  removeBlock: (id) => set((s) => ({
    blocks: s.blocks.filter((b) => b.id !== id),
  })),

  setDraftResult: (landingId, slug) => set({
    draft_landing_id: landingId,
    draft_slug: slug,
  }),

  setLoading: (isLoading) => set({ isLoading }),
  setError: (error) => set({ error }),

  setPromocode: (promocode) => set({ promocode }),
  setPromocodeValid: (valid, remaining = 0) => set({
    promocodeValid: valid,
    promocodeRemaining: remaining,
  }),

  reset: () => set(initialState),

  getLandingData: () => {
    const s = get();
    return {
      business_name: s.business_name,
      business_description: s.business_description,
      phone: s.phone,
      whatsapp: s.whatsapp,
      instagram: s.instagram,
      address: s.address,
      work_hours: s.work_hours,
      items: s.items.filter((i) => i.name && i.price),
      bg_color: s.bg_color,
      text_color: s.text_color,
      title_color: s.title_color,
    };
  },

  getComponents: () => {
    const s = get();
    // Для MVP — фиксированный набор компонентов в определённом порядке
    return [
      {
        component_id: 1, // Header
        position: 1,
        data: {
          business_name: s.business_name,
          business_description: s.business_description,
          bg_color: s.bg_color,
          text_color: s.text_color,
        },
      },
      {
        component_id: 2, // Price list
        position: 2,
        data: {
          section_title: 'Наши услуги',
          col1_title: 'Услуга',
          col2_title: 'Цена',
          items: s.items.filter((i) => i.name && i.price),
          bg_color: '#ffffff',
          title_color: s.title_color,
          header_bg: s.bg_color,
          header_color: s.text_color,
        },
      },
      {
        component_id: 3, // Contacts
        position: 3,
        data: {
          section_title: 'Контакты',
          phone: s.phone,
          whatsapp: s.whatsapp,
          instagram: s.instagram,
          address: s.address,
          work_hours: s.work_hours,
          bg_color: '#f8f9fa',
          text_color: '#333333',
          title_color: s.bg_color,
        },
      },
      {
        component_id: 4, // Footer
        position: 4,
        data: {
          footer_text: `© ${new Date().getFullYear()} ${s.business_name}`,
          bg_color: s.bg_color,
          text_color: s.text_color,
        },
      },
    ];
  },
}));
